import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets-jai6qajn.emergentagent.net/job_031c01f6-1122-4ae8-87ee-69acab3ae0f1/artifacts/kw4zjnmy_ava.jpeg";
const HERO_IMAGE = "https://images.unsplash.com/photo-1599325601183-042bed55081c";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success("Check your email!");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="forgot-password-page" className="min-h-screen relative flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img src={HERO_IMAGE} alt="Andaman Beach" className="w-full h-full object-cover" />
        <div className="hero-overlay absolute inset-0"></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 w-full max-w-md mx-4"
      >
        <div className="glass-card rounded-2xl p-8 sm:p-10 shadow-2xl border border-white/20">
          <div className="flex justify-center mb-6">
            <Link to="/home"><img src={LOGO_URL} alt="Andaman Avalon" className="h-16 w-auto" /></Link>
          </div>

          {sent ? (
            <div className="text-center" data-testid="forgot-success">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-serif font-semibold text-primary mb-3">Check Your Email</h1>
              <p className="text-muted-foreground text-sm mb-6">
                If an account with <strong>{email}</strong> exists, we've sent a password reset link. The link expires in 1 hour.
              </p>
              <p className="text-xs text-muted-foreground italic mb-4">
                Didn't receive it? Check your spam folder or wait a minute before requesting again.
              </p>
              <Link to="/login" className="inline-flex items-center text-secondary font-semibold hover:underline">
                <ArrowLeft size={14} className="mr-1" /> Back to Login
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <h1 className="text-2xl font-serif font-semibold text-primary mb-2">Forgot Password?</h1>
                <p className="text-muted-foreground text-sm">Enter your email and we'll send you a reset link</p>
              </div>

              <form onSubmit={handleSubmit} data-testid="forgot-form" className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      type="email"
                      data-testid="forgot-email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="you@example.com"
                      className="pl-10"
                    />
                  </div>
                </div>

                <Button type="submit" data-testid="forgot-submit-btn" disabled={loading} className="w-full bg-primary hover:bg-primary/90 h-11">
                  {loading ? "Sending..." : "Send Reset Link"}
                </Button>
              </form>

              <div className="mt-5 text-center">
                <Link to="/login" data-testid="back-to-login" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary">
                  <ArrowLeft size={14} className="mr-1" /> Back to Login
                </Link>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
